import Passage from '@app/components/ui/passage';
import { BunnyFace } from '@app/components/ui/bunny';
import PlaneIcon from '@app/components/ui/plane-icon';
import { buildFlightPath } from '@app/components/ui/flight-path';
import useMeasuredWidth from '@app/lib/hooks/use-measured-width';
import { TimelineEntryDto } from '@app/models/dtos/contentDto';
import { motion, useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';

export default function Story({ entries }: { entries: TimelineEntryDto[] }) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const pathRef = useRef<SVGPathElement | null>(null);
    const reduced = useReducedMotion();

    const width = useMeasuredWidth(wrapRef);
    const geometry = useMemo(() => buildFlightPath(width, entries.length), [width, entries.length]);
    const compact = width < 640;

    const [pathLength, setPathLength] = useState(0);

    const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start 0.85', 'end 0.45'] });
    const progress = useSpring(scrollYProgress, { stiffness: 140, damping: 32, restDelta: 0.0005 });

    const planeX = useMotionValue(geometry.points[0]?.x ?? 0);
    const planeY = useMotionValue(0);
    const planeAngle = useMotionValue(90);
    const [flownTo, setFlownTo] = useState(reduced ? 1 : 0);

    const dashOffset = useTransform(progress, (p) => pathLength * (1 - p));

    useMotionValueEvent(progress, 'change', (p) => {
        const path = pathRef.current;
        if (!path || pathLength === 0) return;

        const clamped = Math.min(Math.max(p, 0), 1);
        const at = path.getPointAtLength(pathLength * clamped);
        const ahead = path.getPointAtLength(Math.min(pathLength * clamped + 2, pathLength));
        const dx = ahead.x - at.x;
        const dy = ahead.y - at.y;

        planeX.set(at.x);
        planeY.set(at.y);
        if (dx !== 0 || dy !== 0) planeAngle.set((Math.atan2(dy, dx) * 180) / Math.PI);
        // Under reduced motion the whole route reads as already flown.
        if (!reduced) setFlownTo(clamped);
    });

    const onPathReady = (node: SVGPathElement | null) => {
        pathRef.current = node;
        if (node) {
            setPathLength(node.getTotalLength());
            const start = node.getPointAtLength(0);
            planeX.set(start.x);
            planeY.set(start.y);
        }
    };

    return (
        <Passage id="story" heading="How I got here">
            <div ref={wrapRef} className="relative w-full" style={{ height: geometry.height }}>
                <svg className="pointer-events-none absolute inset-0 max-w-full" width={geometry.width} height={geometry.height} aria-hidden="true">
                    <path d={geometry.d} fill="none" stroke="var(--hairline)" strokeWidth={1.5} strokeLinecap="round" strokeDasharray="1 7" />
                    <motion.path
                        key={geometry.d}
                        ref={onPathReady}
                        d={geometry.d}
                        fill="none"
                        stroke="var(--accent)"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeDasharray={pathLength || undefined}
                        // Until the route has been measured there is no dash to hide it,
                        // so it would otherwise render fully flown on the server.
                        strokeOpacity={pathLength ? 1 : 0}
                        style={reduced ? { strokeDashoffset: 0 } : { strokeDashoffset: dashOffset }}
                    />
                    {geometry.points.map((point, index) => {
                        const reached = flownTo >= (index + 0.5) / entries.length - 0.06;
                        return <circle key={index} cx={point.x} cy={point.y} r={3} fill={reached ? 'var(--accent)' : 'var(--paper)'} stroke={reached ? 'var(--accent)' : 'var(--hairline)'} strokeWidth={1.5} className="transition-all duration-500" />;
                    })}
                </svg>

                {!reduced && pathLength > 0 && (
                    <motion.div className="pointer-events-none absolute left-0 top-0 z-10 text-accent" style={{ x: planeX, y: planeY, rotate: planeAngle, translateX: '-50%', translateY: '-50%' }} aria-hidden="true">
                        <PlaneIcon className="h-[18px] w-[18px]" />
                    </motion.div>
                )}

                {/* Waiting at the destination. */}
                {(() => {
                    const last = geometry.points[geometry.points.length - 1];
                    if (!last) return null;
                    const arrived = flownTo > 0.9;
                    return (
                        <motion.span
                            className="pointer-events-none absolute z-10"
                            style={{ left: last.x - 15, top: last.y + 16 }}
                            initial={false}
                            animate={{ opacity: arrived ? 1 : 0, y: arrived ? 0 : 8 }}
                            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            aria-hidden="true"
                        >
                            <BunnyFace size={30} />
                        </motion.span>
                    );
                })()}

                <ol className="absolute inset-0">
                    {entries.map((entry, index) => {
                        const point = geometry.points[index];
                        if (!point) return null;

                        const reached = flownTo >= (index + 0.5) / entries.length - 0.06;
                        const toRight = compact || point.side === 'right';

                        return (
                            <li
                                key={entry.year}
                                className="absolute"
                                style={{
                                    top: point.y,
                                    left: toRight ? point.x + (compact ? 22 : 30) : undefined,
                                    right: toRight ? undefined : geometry.width - point.x + 30,
                                    width: geometry.labelWidth,
                                    transform: 'translateY(-50%)'
                                }}
                            >
                                <motion.div
                                    className={toRight ? undefined : 'text-right'}
                                    initial={{ opacity: 0, y: reduced ? 0 : 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.6 }}
                                    transition={{ duration: reduced ? 0.2 : 0.45, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <p className={`t-label transition-colors duration-500 ${reached ? 'text-accent' : 'text-muted'}`}>{entry.year}</p>
                                    <p className="t-small mt-2 text-body">{entry.text}</p>
                                </motion.div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </Passage>
    );
}
